import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { Calendar } from '@mantine/dates';
import { XIcon, CalendarX2 } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, getISOWeek, startOfMonth, endOfMonth, eachWeekOfInterval } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../contexts';
import absenceVacationService, { type DateUpdate } from '../services/absenceVacationService';
import { timeEntryService } from '../services/timeEntryService';
import { colors } from '../theme/colors';
import type { EmployeeLoggedStatus } from '../data/databaseSchema';
import { getCalendarWeek } from '../utils/dateUtils';

interface AbsenceVacationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (updatedDates?: string[]) => void;
  monthlyStatus?: EmployeeLoggedStatus[];
  currentMonth?: { month: number; year: number };
  onMonthChange?: (month: number, year: number) => void;
}

type DateStatus = 'absence' | 'vacation' | 'bridge' | 'normal' | null;

export const AbsenceVacationDialog: React.FC<AbsenceVacationDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  monthlyStatus = [],
  currentMonth,
  onMonthChange,
}) => {
  const { employeeId } = useSession();
  const queryClient = useQueryClient();
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [datePreviews, setDatePreviews] = useState<Map<string, DateStatus>>(new Map());
  const [currentDate, setCurrentDate] = useState<string>(() => {
    const initialDate = currentMonth 
      ? format(new Date(currentMonth.year, currentMonth.month - 1, 1), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');
    return initialDate;
  });
  const [selectedAction, setSelectedAction] = useState<DateStatus | 'normal' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warning, setWarning] = useState<{ has_existing_hours: boolean; dates_with_hours: string[] } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<DateUpdate[]>([]);

  const originalOverflowRef = useRef<string | null>(null);
  const isUserNavigationRef = useRef<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      originalOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      setSelectedDates(new Set());
      setDatePreviews(new Map());
      setSelectedAction(null);
      setWarning(null);
      setShowConfirmDialog(false);
      setPendingUpdates([]);
      if (currentMonth && !isUserNavigationRef.current) {
        const newDate = format(new Date(currentMonth.year, currentMonth.month - 1, 1), 'yyyy-MM-dd');
        setCurrentDate(newDate);
      }
      isUserNavigationRef.current = false;
    } else {
      if (originalOverflowRef.current !== null) {
        document.body.style.overflow = originalOverflowRef.current;
        originalOverflowRef.current = null;
      } else {
        document.body.style.removeProperty('overflow');
      }
      isUserNavigationRef.current = false;
    }
  }, [isOpen, currentMonth]);

  const handleDateClick = useCallback((date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    setSelectedDates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dateStr)) {
        newSet.delete(dateStr);
      } else {
        newSet.add(dateStr);
      }
      if (newSet.size === 0) {
        setSelectedAction(null);
      }
      return newSet;
    });
  }, []);

  const handleUpdate = useCallback(async () => {
    if (datePreviews.size === 0) {
      return;
    }

    const updates: DateUpdate[] = [];
    datePreviews.forEach((status, dateStr) => {
      if (status === 'absence' || status === 'vacation' || status === 'bridge') {
        updates.push({ date: dateStr, status });
      } else if (status === 'normal') {
        updates.push({ date: dateStr, status: 'remove' });
      }
    });

    if (updates.length === 0) {
      return;
    }

    const dates = updates.map((u) => u.date);
    try {
      const checkResult = await absenceVacationService.checkExistingHours(employeeId, dates);
      if (checkResult.has_existing_hours) {
        setWarning({
          has_existing_hours: checkResult.has_existing_hours,
          dates_with_hours: checkResult.dates_with_hours,
        });
        setPendingUpdates(updates);
        setShowConfirmDialog(true);
      } else {
        await handleConfirmUpdate(updates);
      }
    } catch (error) {
      console.error('Failed to check existing hours:', error);
      alert('Failed to check existing hours. Please try again.');
    }
  }, [datePreviews, employeeId]);

  const handleConfirmUpdate = useCallback(async (updates: DateUpdate[]) => {
    setIsSubmitting(true);
    try {
      await absenceVacationService.processBatchUpdates(employeeId, updates);
      
      const updatedDates = updates.map(u => u.date);
      
      updates.forEach((update) => {
        const dateObj = new Date(update.date);
        const calendarWeek = getCalendarWeek(dateObj);
        queryClient.invalidateQueries({ 
          queryKey: ['timeEntries', calendarWeek, employeeId] 
        });
      });
      
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['monthlyStatus'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntriesStatus'] });
      
      setDatePreviews(new Map());
      setSelectedDates(new Set());
      setWarning(null);
      setShowConfirmDialog(false);
      setPendingUpdates([]);
      onSuccess?.(updatedDates);
      onClose();
    } catch (error) {
      console.error('Failed to update absence/vacation:', error);
      alert('Failed to update absence/vacation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [employeeId, onSuccess, onClose, queryClient]);

  const summary = useMemo(() => {
    let absenceCount = 0;
    let vacationCount = 0;
    let bridgeCount = 0;
    let removeCount = 0;

    datePreviews.forEach((status) => {
      if (status === 'absence') absenceCount++;
      else if (status === 'vacation') vacationCount++;
      else if (status === 'bridge') bridgeCount++;
      else if (status === 'normal') removeCount++;
    });

    return { absenceCount, vacationCount, bridgeCount, removeCount };
  }, [datePreviews]);

  const period = useMemo(() => {
    const currentDateObj = new Date(currentDate);
    const year = currentDateObj.getFullYear();
    const month = String(currentDateObj.getMonth() + 1).padStart(2, '0');
    const periodValue = `${year}-${month}`;
    return periodValue;
  }, [currentDate]);

  const {
    data: statusData,
  } = useQuery({
    queryKey: ['monthlyStatus', period, employeeId],
    queryFn: () => timeEntryService.getTimeEntriesStatus(employeeId, period),
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: !!employeeId && isOpen,
  });

  const weekRecord = useMemo(() => {
    return statusData?.data?.week || {};
  }, [statusData]);

  const apiMonthlyStatus = useMemo(() => {
    return statusData?.data?.day || [];
  }, [statusData]);

  const effectiveMonthlyStatus = useMemo(() => {
    return apiMonthlyStatus.length > 0 ? apiMonthlyStatus : monthlyStatus;
  }, [apiMonthlyStatus, monthlyStatus]);

  const holidayMap = useMemo(() => {
    const map = new Map<string, boolean>();
    effectiveMonthlyStatus.forEach((status) => {
      if (status.is_holiday) {
        map.set(status.date_of_year, true);
      }
    });
    return map;
  }, [effectiveMonthlyStatus]);

  const noPlannedHoursMap = useMemo(() => {
    const map = new Map<string, boolean>();
    effectiveMonthlyStatus.forEach((status) => {
      const statusWithPlannedHours = status as EmployeeLoggedStatus;
      if (statusWithPlannedHours.has_planned_hours === false) {
        map.set(status.date_of_year, true);
      }
    });
    return map;
  }, [effectiveMonthlyStatus]);

  const closedDateMap = useMemo(() => {
    const map = new Map<string, boolean>();
    effectiveMonthlyStatus.forEach((status) => {
      let calendarWeek = status.calendar_week;
      
      if (!calendarWeek) {
        try {
          const dateObj = new Date(status.date_of_year);
          calendarWeek = getCalendarWeek(dateObj);
        } catch (e) {
          // If date parsing fails, skip this entry
        }
      }
      
      if (calendarWeek && weekRecord[calendarWeek] === 'close') {
        map.set(status.date_of_year, true);
      } else if (status.status === 'close' || status.status === 'closed') {
        map.set(status.date_of_year, true);
      }
    });
    return map;
  }, [effectiveMonthlyStatus, weekRecord]);

  const existingStatusMap = useMemo(() => {
    const map = new Map<string, DateStatus>();
    effectiveMonthlyStatus.forEach((status) => {
      if (status.is_absence) {
        map.set(status.date_of_year, 'absence');
      } else if (status.is_vacation) {
        map.set(status.date_of_year, 'vacation');
      } else if (status.is_bridge) {
        map.set(status.date_of_year, 'bridge');
      }
    });
    return map;
  }, [effectiveMonthlyStatus]);

  const handleActionChange = useCallback((action: DateStatus | 'normal') => {
    const newAction = selectedAction === action ? null : action;
    setSelectedAction(newAction);

    if (selectedDates.size > 0 && newAction) {
      setDatePreviews((prev) => {
        const newMap = new Map(prev);
        selectedDates.forEach((dateStr) => {
          const previewStatus = newMap.get(dateStr);
          const existingStatus = existingStatusMap.get(dateStr);
          const currentStatus = previewStatus !== undefined ? previewStatus : existingStatus;

          if (newAction === 'absence' || newAction === 'vacation' || newAction === 'bridge') {
            if (currentStatus === newAction) {
              newMap.set(dateStr, 'normal');
            } else {
              newMap.set(dateStr, newAction);
            }
          } else if (newAction === 'normal') {
            newMap.set(dateStr, 'normal');
          }
        });
        return newMap;
      });
      setSelectedDates(new Set());
    }
  }, [selectedDates, selectedAction, existingStatusMap]);

  const getDateStatus = useCallback((date: Date): DateStatus => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const previewStatus = datePreviews.get(dateStr);
    if (previewStatus !== undefined) {
      return previewStatus;
    }
    return existingStatusMap.get(dateStr) || null;
  }, [datePreviews, existingStatusMap]);

  const isHoliday = useCallback((date: Date): boolean => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidayMap.get(dateStr) ?? false;
  }, [holidayMap]);

  const isClosed = useCallback((date: Date): boolean => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return closedDateMap.get(dateStr) ?? false;
  }, [closedDateMap]);

  const hasNoPlannedHours = useCallback((date: Date): boolean => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return noPlannedHoursMap.get(dateStr) ?? false;
  }, [noPlannedHoursMap]);

  const isDateSelected = useCallback((date: Date): boolean => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return selectedDates.has(dateStr);
  }, [selectedDates]);

  const getDayProps = useCallback((date: string) => {
    const dateObj = new Date(date);
    const status = getDateStatus(dateObj);
    const isSelected = isDateSelected(dateObj);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateObjNormalized = new Date(dateObj);
    dateObjNormalized.setHours(0, 0, 0, 0);
    const isToday = dateObjNormalized.getTime() === today.getTime();
    const dayOfWeek = dateObj.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
    const holiday = isHoliday(dateObj);
    const closed = isClosed(dateObj);
    const noPlannedHours = hasNoPlannedHours(dateObj);
    const isCurrentMonth = dateObj.getMonth() === new Date(currentDate).getMonth() &&
      dateObj.getFullYear() === new Date(currentDate).getFullYear();

    let backgroundColor: string | undefined;
    let borderColor: string | undefined;
    let fontWeight: string | undefined;
    let opacity: number | undefined;
    let cursor: string | undefined;
    let textColor: string | undefined;
    let tooltip: string | undefined;
    let padding: string | undefined;

    // Priority 1: Holiday - gray pastel (highest priority)
    if (holiday && isCurrentMonth) {
      backgroundColor = colors.neutral.grey[200];
      textColor = colors.neutral.grey[500];
      opacity = 0.7;
      cursor = 'not-allowed';
      tooltip = 'Bank Holiday';
    }
    // Priority 2: Closed week - disable selection but allow status colors
    else if (closed && isCurrentMonth) {
      cursor = 'not-allowed';
      tooltip = 'week is closed';
    }
    // Priority 3: Weekend
    else if (isWeekend) {
      opacity = 0.5;
      cursor = 'not-allowed';
    }
    // Priority 4: No Planned Hours
    else if (noPlannedHours && isCurrentMonth) {
      textColor = colors.neutral.grey[500];
      opacity = 0.5;
      cursor = 'not-allowed';
      tooltip = 'No Planned Hours';
    }

    // Priority 5: Absence/Vacation/Bridge status (show even if closed, but NOT if no planned hours)
    if (!noPlannedHours) {
      if (status === 'absence') {
        backgroundColor = colors.pastel.brown;
        textColor = colors.neutral.grey[800];
      } else if (status === 'vacation') {
        backgroundColor = colors.pastel.brownDark;
        textColor = colors.neutral.grey[800];
      } else if (status === 'bridge') {
        backgroundColor = '#faa1c9';
        textColor = colors.neutral.grey[800];
      }
    }
    // Priority 5: Visual feedback for selected dates (when no status)
    else if (isSelected && !status && !closed) {
      backgroundColor = colors.accent.blue + '20';
    }

    // Selected border (takes priority, shown even with absence/vacation status, but not for holiday/closed/no planned hours)
    if (isSelected && !holiday && !closed && !noPlannedHours) {
      borderColor = colors.accent.blue;
    }
    // Border priority: Today (show even if closed, but not if holiday or selected)
    else if (!isSelected && isToday) {
      borderColor = `${colors.accent.blue}50`;
    }

    if (isToday && !closed) {
      fontWeight = '600';
    }

    if (isToday && closed) {
      padding = '2px';
    }

    const isDisabled = isWeekend || holiday || closed || noPlannedHours;

    return {
      onClick: isDisabled ? undefined : () => handleDateClick(dateObj),
      disabled: isDisabled,
      title: tooltip,
      style: {
        display: isCurrentMonth ? undefined : 'none',
        backgroundColor,
        border: borderColor ? `2px solid ${borderColor}` : undefined,
        color: textColor || (backgroundColor ? colors.neutral.grey[800] : undefined),
        fontWeight,
        opacity,
        cursor,
        padding,
      },
    };
  }, [getDateStatus, isDateSelected, handleDateClick, isHoliday, isClosed, hasNoPlannedHours, currentDate]);

  const renderDay = useCallback((date: string) => {
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const today = new Date();
    const isToday = dateObj.getDate() === today.getDate() &&
      dateObj.getMonth() === today.getMonth() &&
      dateObj.getFullYear() === today.getFullYear();
    const dateStr = format(dateObj, 'yyyy-MM-dd');
    const closed = closedDateMap.get(dateStr) ?? false;
    const noPlannedHours = noPlannedHoursMap.get(dateStr) ?? false;
    const isCurrentMonth = dateObj.getMonth() === new Date(currentDate).getMonth() &&
      dateObj.getFullYear() === new Date(currentDate).getFullYear();
    const dayOfWeek = dateObj.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (!isCurrentMonth) {
      return null;
    }

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {day}
        {isToday && isCurrentMonth && (
          <div
            style={{
              position: 'absolute',
              width: '1.5rem',
              height: '2px',
              backgroundColor: colors.accent.blue,
              bottom: closed || noPlannedHours ? '0.4rem' : '0.2rem',
              zIndex: 3,
            }}
          />
        )}
        {closed && isCurrentMonth && !isWeekend && (
          <div
            style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              backgroundColor: colors.accent.green,
              borderRadius: '50%',
              bottom: isToday ? '0' : '0.2rem',
              zIndex: 3,
            }}
          />
        )}
      </div>
    );
  }, [closedDateMap, noPlannedHoursMap, currentDate]);

  const weekNumbers = useMemo(() => {
    const currentDateObj = new Date(currentDate);
    const monthStart = startOfMonth(currentDateObj);
    const monthEnd = endOfMonth(currentDateObj);
    const weekStarts = eachWeekOfInterval(
      { start: monthStart, end: monthEnd },
      { weekStartsOn: 1 }
    );
    return weekStarts.map((weekStart) => ({
      weekNum: getISOWeek(weekStart).toString().padStart(2, '0'),
      weekStart,
    }));
  }, [currentDate]);

  const getWeekStatus = useCallback((weekStart: Date): 'close' | 'open' => {
    const calendarWeekStr = getCalendarWeek(weekStart);
    
    if (weekRecord[calendarWeekStr]) {
      const weekStatus = weekRecord[calendarWeekStr];
      return weekStatus === 'close' ? 'close' : 'open';
    }

    const weekDays = effectiveMonthlyStatus.filter((status) => status.calendar_week === calendarWeekStr);
    
    if (weekDays.length === 0) {
      return 'open';
    }

    const hasClosedDay = weekDays.some((day) => day.status === 'close' || day.status === 'closed');
    return hasClosedDay ? 'close' : 'open';
  }, [weekRecord, effectiveMonthlyStatus]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          }}
        />
        <Dialog.Positioner
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Dialog.Content
            style={{
              backgroundColor: colors.neutral.white,
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '85vh',
              height: 'auto',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <Dialog.Title style={{ fontSize: '24px', fontWeight: 'bold', color: colors.primary.textDark }}>
                Bridge Days
              </Dialog.Title>
              <Dialog.CloseTrigger
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <XIcon size={24} />
              </Dialog.CloseTrigger>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <p style={{ color: colors.secondary.text, marginBottom: '12px' }}>
                Click multiple dates to select them, then choose an action. Absence and vacation are taken from ZM.
              </p>
              {selectedDates.size > 0 && (
                <p style={{ color: colors.accent.blue, fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>
                  {selectedDates.size} date{selectedDates.size > 1 ? 's' : ''} selected
                </p>
              )}
              {selectedDates.size === 0 && (
                <p style={{ color: colors.neutral.white, fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>
                  ..
                </p>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '50px', marginBottom: '20px' }}>
              <div
                style={{
                  border: `1px solid ${colors.neutral.grey[200]}`,
                  borderRadius: '8px',
                  padding: '16px',
                  height: '440px',
                  overflow: 'auto',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '55px' }}>
                    <div
                      style={{
                        height: '40px',
                        minHeight: '40px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: colors.secondary.text,
                        padding: '2px',
                        width: '20px',
                      }}
                    >
                      CW
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
                      {weekNumbers.map((week, index) => {
                        const weekStatus = getWeekStatus(week.weekStart);
                        const weekBgColor = weekStatus === 'close' 
                          ? colors.pastel.greenWeek
                          : colors.pastel.yellowWeek;
                        
                        return (
                          <div
                            key={index}
                            style={{
                              height: '40px',
                              minHeight: '38px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: colors.secondary.text,
                              padding: '2px',
                              width: '32px',
                              backgroundColor: weekBgColor,
                              borderRadius: '0.1rem',
                            }}
                          >
                            {week.weekNum}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Calendar
                      date={currentDate}
                      onDateChange={(date) => {
                        const dateObj = new Date(date);
                        const newMonth = dateObj.getMonth() + 1;
                        const newYear = dateObj.getFullYear();
                        const currentDateObj = new Date(currentDate);
                        const currentMonth = currentDateObj.getMonth() + 1;
                        const currentYear = currentDateObj.getFullYear();
                        
                        const monthChanged = newMonth !== currentMonth || newYear !== currentYear;
                        
                        setCurrentDate(date);
                        
                        if (onMonthChange && monthChanged) {
                          isUserNavigationRef.current = true;
                          onMonthChange(newMonth, newYear);
                        }
                      }}
                      getDayProps={getDayProps}
                      renderDay={renderDay}
                      firstDayOfWeek={1}
                      styles={{
                        calendarHeader: {
                          marginBottom: '12px',
                          position: 'relative',
                        },
                        month: {
                          width: '100%',
                        },
                        monthCell: {
                          padding: '4px',
                          width: 'calc(100% / 7)',
                        },
                        weekday: {
                          padding: '8px 4px',
                          textAlign: 'center',
                          width: 'calc(100% / 7)',
                        },
                        weekdaysRow: {
                          width: '100%',
                        },
                        day: {
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          minWidth: '40px',
                          minHeight: '40px',
                          margin: '2px',
                          width: '100%',
                        },
                      }}
                    />
                  </div>
                </div>
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '16px',
                  zIndex: 10,
                }}>
                  {(() => {
                    const today = new Date();
                    const currentDateObj = new Date(currentDate);
                    const isCurrentMonth = currentDateObj.getMonth() === today.getMonth() &&
                      currentDateObj.getFullYear() === today.getFullYear();
                    const isDisabled = isCurrentMonth;

                    return (
                      <button
                        type="button"
                        onClick={() => setCurrentDate(format(new Date(), 'yyyy-MM-dd'))}
                        disabled={isDisabled}
                        style={{
                          backgroundColor: isDisabled ? colors.neutral.grey[300] : colors.accent.blue,
                          color: isDisabled ? colors.neutral.grey[600] : colors.neutral.white,
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.6rem 1rem',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.backgroundColor = colors.accent.blue + 'CC';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.backgroundColor = colors.accent.blue;
                          }
                        }}
                      >
                        Today
                      </button>
                    );
                  })()}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDates(new Set());
                    setSelectedAction(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: colors.neutral.grey[200],
                    color: colors.neutral.grey[700],
                    border: `1px solid ${colors.neutral.grey[300]}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '14px',
                    marginBottom: '8px',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.neutral.grey[300];
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.neutral.grey[200];
                  }}
                >
                  <CalendarX2 size={16} />
                  Clear Selection
                </button>
                <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: colors.primary.textDark }}>
                  Options
                </h3>


                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: selectedDates.size > 0 ? 'pointer' : 'not-allowed',
                      opacity: selectedDates.size > 0 ? 1 : 0.5,
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        width: '18px',
                        height: '18px',
                        flexShrink: 0,
                        paddingLeft: '4px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAction === 'bridge'}
                        onChange={() => handleActionChange('bridge')}
                        disabled={selectedDates.size === 0}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: selectedDates.size > 0 ? 'pointer' : 'not-allowed',
                          margin: 0,
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          backgroundColor: '#faa1c9',
                          border: `2px solid #faa1c9`,
                          borderRadius: '4px',
                          position: 'relative',
                        }}
                      />
                      {selectedAction === 'bridge' && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: colors.neutral.grey[800],
                            fontSize: '12px',
                            fontWeight: 'bold',
                            lineHeight: '1',
                            paddingLeft: '5px',
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                    <span style={{ marginLeft: '6px', color: colors.secondary.text, fontWeight: '500' }}>Bridge Day</span>
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: selectedDates.size > 0 ? 'pointer' : 'not-allowed',
                      opacity: selectedDates.size > 0 ? 1 : 0.5,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAction === 'normal'}
                      onChange={() => handleActionChange('normal')}
                      disabled={selectedDates.size === 0}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: selectedDates.size > 0 ? 'pointer' : 'not-allowed',
                      }}
                    />
                    <span style={{ color: colors.secondary.text, fontWeight: '500' }}>Reset Normal Day</span>
                  </label>
                </div>

                {datePreviews.size > 0 && (
                  <div style={{ marginTop: '24px', padding: '16px', backgroundColor: colors.neutral.grey[50], borderRadius: '4px', maxWidth: '100%', boxSizing: 'border-box' }}>
                    <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Summary of Changes</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                      {summary.absenceCount > 0 && (
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          <span style={{ fontWeight: '600' }}>{summary.absenceCount}</span> date(s) marked as <strong>Absence</strong>
                        </div>
                      )}
                      {summary.vacationCount > 0 && (
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          <span style={{ fontWeight: '600' }}>{summary.vacationCount}</span> date(s) marked as <strong>Vacation</strong>
                        </div>
                      )}
                      {summary.bridgeCount > 0 && (
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          <span style={{ fontWeight: '600' }}>{summary.bridgeCount}</span> date(s) marked as <strong>Bridge Day</strong>
                        </div>
                      )}
                      {summary.removeCount > 0 && (
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          <span style={{ fontWeight: '600' }}>{summary.removeCount}</span> date(s) to be <strong>Reset Normal Day</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: 'auto' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: colors.neutral.grey[300],
                      color: colors.neutral.grey[700],
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdate}
                    disabled={datePreviews.size === 0 || isSubmitting}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: datePreviews.size > 0 && !isSubmitting ? colors.accent.blue : colors.neutral.grey[300],
                      color: datePreviews.size > 0 && !isSubmitting ? colors.neutral.white : colors.neutral.grey[600],
                      border: 'none',
                      borderRadius: '4px',
                      cursor: datePreviews.size > 0 && !isSubmitting ? 'pointer' : 'not-allowed',
                      fontWeight: '500',
                    }}
                  >
                    {isSubmitting ? 'Updating...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>

            {warning && warning.has_existing_hours && (
              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: colors.pastel.yellow, borderRadius: '4px' }}>
                <h3 style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: colors.accent.yellow }}>
                  Warning: Existing Logged Hours
                </h3>
                <p style={{ marginBottom: '8px', color: colors.secondary.text }}>
                  The following dates already have logged hours:
                </p>
                <ul style={{ marginLeft: '20px', color: colors.secondary.text }}>
                  {warning.dates_with_hours.map((date: string) => (
                    <li key={date}>{date}</li>
                  ))}
                </ul>
                <p style={{ marginTop: '8px', color: colors.secondary.text, fontSize: '14px' }}>
                  The original entries will be overridden for these dates.
                </p>
              </div>
            )}



            {showConfirmDialog && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 2000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    backgroundColor: colors.neutral.white,
                    borderRadius: '8px',
                    padding: '24px',
                    maxWidth: '500px',
                    width: '90%',
                  }}
                >
                  <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
                    Confirm Override
                  </h3>
                  <p style={{ marginBottom: '20px', color: colors.secondary.text }}>
                    These dates already have entries. the entries will be overridden and not able to be recovered. Continue?
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowConfirmDialog(false);
                        setWarning(null);
                        setPendingUpdates([]);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: colors.neutral.grey[300],
                        color: colors.neutral.grey[700],
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '500',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleConfirmUpdate(pendingUpdates)}
                      disabled={isSubmitting}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: colors.accent.blue,
                        color: colors.neutral.white,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        fontWeight: '500',
                      }}
                    >
                      {isSubmitting ? 'Processing...' : 'Confirm'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
